<template>
    <div class="dt-filter-sidebar" :style="sidebarStyle">
        <div class="dt-filter-sidebar-header">
            <svg class="dt-filter-sidebar-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span class="dt-filter-sidebar-title">{{ $t('common.dataTable.filters') }}</span>
            <button v-if="hasActiveFilters" class="dt-filter-sidebar-clear" @click="$emit('clear')">
                {{ $t('common.dataTable.clearFilters') }}
            </button>
        </div>
        <div class="dt-filter-sidebar-body">
            <div v-for="f in filters" :key="f.key" class="dt-filter-group">
                <label class="dt-filter-label">{{ f.label }}</label>
                <template v-if="f.type === 'text'">
                    <div class="dt-filter-input-wrap">
                        <svg class="dt-filter-input-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input class="dt-filter-input" :class="{'dt-filter-input--has-clear': filterValues[f.key]}" :placeholder="f.placeholder || ''" :value="filterValues[f.key] || ''" @input="onInput(f.key, $event.target.value)"/>
                        <button v-if="filterValues[f.key]" class="dt-filter-input-clear" @click="$emit('filter', f.key, '')">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                </template>
                <template v-else-if="f.type === 'select'">
                    <div class="dt-filter-custom-select">
                        <button :ref="'selectBtn_' + f.key" class="dt-filter-custom-select-btn" @click.stop="toggleSelect(f.key)">
                            <span>{{ filterValues[f.key] ? getOptionLabel(f, filterValues[f.key]) : $t('common.chooseSelectShort') }}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        <div v-if="openSelectKey === f.key" class="dt-filter-custom-select-menu" :style="selectDropdownStyle">
                            <button class="dt-filter-custom-select-opt" :class="{'dt-filter-custom-select-opt--active': !filterValues[f.key]}" @click.stop="onSelectOption(f.key, '')">
                                <svg v-if="!filterValues[f.key]" class="dt-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                <span v-else class="dt-select-check-space"></span>
                                <span>{{ $t('common.chooseSelectShort') }}</span>
                            </button>
                            <button v-for="opt in f.options" :key="opt.value" class="dt-filter-custom-select-opt" :class="{'dt-filter-custom-select-opt--active': filterValues[f.key] === opt.value}" @click.stop="onSelectOption(f.key, opt.value)">
                                <svg v-if="filterValues[f.key] === opt.value" class="dt-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                <span v-else class="dt-select-check-space"></span>
                                <span>{{ opt.label }}</span>
                            </button>
                        </div>
                    </div>
                </template>
                <template v-else-if="f.type === 'daterange'">
                    <div class="dt-filter-daterange">
                        <DateTimePicker
                            :model-value="(filterValues[f.key] || {}).from || null"
                            type="date"
                            :placeholder="$t('common.dataTable.dateFrom')"
                            :max="(filterValues[f.key] || {}).to || null"
                            clearable
                            :teleported="true"
                            @change="onDateRange(f.key, 'from', $event)"/>
                        <DateTimePicker
                            :model-value="(filterValues[f.key] || {}).to || null"
                            type="date"
                            :placeholder="$t('common.dataTable.dateTo')"
                            :min="(filterValues[f.key] || {}).from || null"
                            clearable
                            :teleported="true"
                            @change="onDateRange(f.key, 'to', $event)"/>
                    </div>
                </template>
            </div>
        </div>
        <div class="dt-filter-sidebar-divider" @mousedown="$emit('divider-start', $event)" @touchstart.prevent="$emit('divider-start', $event)"></div>
    </div>
</template>

<script>
import DateTimePicker from '@/components/common/date-time-picker/DateTimePicker.vue'

export default {
    name: 'DataTableFilterSidebar',
    components: { DateTimePicker },
    props: {
        filters: Array,
        filterValues: Object,
        width: Number,
        isCompact: Boolean,
    },
    emits: ['filter', 'clear', 'divider-start'],
    data() {
        return {
            openSelectKey: null,
            selectDropdownStyle: null,
        }
    },
    created() {
        this.debounceTimers = {}
        this._closeHandlers = null
    },
    computed: {
        sidebarStyle() {
            if (this.isCompact) return null
            return { width: this.width + 'px' }
        },
        hasActiveFilters() {
            return Object.values(this.filterValues).some(v => {
                if (v && typeof v === 'object') return Object.values(v).some(Boolean)
                return Boolean(v)
            })
        },
    },
    methods: {
        onInput(key, val) {
            clearTimeout(this.debounceTimers[key])
            this.debounceTimers[key] = setTimeout(() => {
                this.$emit('filter', key, val)
            }, 300)
        },
        onDateRange(key, field, val) {
            const current = this.filterValues[key] || {}
            this.$emit('filter', key, { ...current, [field]: val })
        },
        getOptionLabel(filter, value) {
            const opt = filter.options?.find(o => o.value === value)
            return opt ? opt.label : value
        },
        toggleSelect(key) {
            if (this.openSelectKey === key) {
                this._dismissSelect()
                return
            }
            const ref = this.$refs['selectBtn_' + key]
            const el = Array.isArray(ref) ? ref[0] : ref
            const selectWrap = el?.closest('.dt-filter-custom-select')
            if (el) {
                const rect = el.getBoundingClientRect()
                this.selectDropdownStyle = {
                    position: 'fixed',
                    top: (rect.bottom + 4) + 'px',
                    bottom: 'auto',
                    left: rect.left + 'px',
                    right: 'auto',
                    minWidth: rect.width + 'px',
                    zIndex: 9999,
                }
            }
            this.openSelectKey = key
            const onMousedown = (e) => {
                if (selectWrap && selectWrap.contains(e.target)) return
                this._dismissSelect()
            }
            const onScroll = (e) => {
                const menu = this.$el.querySelector('.dt-filter-custom-select-menu')
                if (menu && (menu === e.target || menu.contains(e.target))) return
                this._dismissSelect()
            }
            this._closeHandlers = { onMousedown, onScroll }
            setTimeout(() => {
                document.addEventListener('mousedown', onMousedown)
                document.addEventListener('scroll', onScroll, true)
            }, 0)
        },
        _dismissSelect() {
            this.openSelectKey = null
            this.selectDropdownStyle = null
            if (this._closeHandlers) {
                document.removeEventListener('mousedown', this._closeHandlers.onMousedown)
                document.removeEventListener('scroll', this._closeHandlers.onScroll, true)
                this._closeHandlers = null
            }
        },
        onSelectOption(key, val) {
            this.$emit('filter', key, val)
            this._dismissSelect()
        },
    },
    beforeUnmount() {
        this._dismissSelect()
    },
}
</script>
